import React from "react";
import { X, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { KIND, PARTICIPANT_COLOR, PARTICIPANT_NAME, scoreTone } from "@/lib/strategy";

export function Panel({ children, className = "", glow = false, style, ...rest }) {
  return <div className={`st-panel ${glow ? "st-glow" : ""} ${className}`} style={style} {...rest}>{children}</div>;
}

export function PageHead({ eyebrow, title, sub, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div>
        {eyebrow && <p className="st-caps mb-2">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
        {sub && <p className="st-muted mt-2 max-w-2xl text-sm leading-relaxed">{sub}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function KindBadge({ kind, small = false }) {
  const k = KIND[kind] || KIND.ASSUMPTION;
  return <span className="st-pill" title={k.hint} style={{ color: k.color, background: k.bg, fontSize: small ? 10 : 11 }} data-testid={`kind-${kind?.toLowerCase()}`}>{k.label}</span>;
}

export function DemoBadge() {
  return <span className="st-pill" style={{ color: KIND.DEMO.color, background: KIND.DEMO.bg }} data-testid="demo-badge">DEMO DATA</span>;
}

export function Avatar({ pid, size = 28 }) {
  return (
    <span className="grid place-items-center rounded-full font-bold shrink-0" style={{ width: size, height: size, fontSize: size * 0.4, background: `${PARTICIPANT_COLOR[pid]}22`, color: PARTICIPANT_COLOR[pid], border: `1px solid ${PARTICIPANT_COLOR[pid]}55` }}>
      {(PARTICIPANT_NAME[pid] || pid)[0]}
    </span>
  );
}

export function ScorePill({ score }) {
  return <span className="st-pill" style={{ color: scoreTone(score), background: `${scoreTone(score)}1f` }}>{score == null ? "n/a" : `${score}%`}</span>;
}

export function Stat({ label, value, hint, tone, testid }) {
  return (
    <div className="st-panel-2 p-5" data-testid={testid}>
      <p className="st-caps">{label}</p>
      <p className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-2" style={{ color: tone }}>{value}</p>
      {hint && <p className="st-faint text-xs mt-1">{hint}</p>}
    </div>
  );
}

export function Bar({ value, color }) {
  return <div className="st-bar"><span style={{ width: `${Math.max(0, Math.min(100, value || 0))}%`, background: color || undefined }} /></div>;
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-3 st-muted text-sm py-16 justify-center" data-testid="st-loading">
      <Loader2 size={18} className="animate-spin" style={{ color: "var(--st-lime)" }} /> {label}
    </div>
  );
}

export function Empty({ title, body, action }) {
  return (
    <Panel className="p-10 text-center" data-testid="st-empty">
      <h3 className="text-lg font-bold">{title}</h3>
      {body && <p className="st-muted text-sm mt-2 max-w-md mx-auto leading-relaxed">{body}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </Panel>
  );
}

export function ErrorNote({ error, retry }) {
  if (!error) return null;
  return (
    <div className="rounded-xl p-4 text-sm flex items-center justify-between gap-3" style={{ background: "rgba(240,122,122,.1)", border: "1px solid rgba(240,122,122,.3)", color: "#F3EFE6" }} data-testid="st-error">
      <span>{error}</span>
      {retry && <button className="st-btn st-btn-ghost !py-1.5 !px-3 text-xs" onClick={retry}>Retry</button>}
    </div>
  );
}

export function Drawer({ open, onClose, title, children, width = 560, testid = "st-drawer" }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-40" style={{ background: "rgba(5,7,10,.6)", backdropFilter: "blur(4px)" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside className="fixed top-0 right-0 bottom-0 z-50 flex flex-col st-root" style={{ width: `min(${width}px, 100vw)`, borderLeft: "1px solid var(--st-line-2)" }}
            initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }} data-testid={testid}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--st-line)" }}>
              <h3 className="font-bold text-base">{title}</h3>
              <button onClick={onClose} className="st-btn st-btn-quiet !p-2" data-testid={`${testid}-close`}><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="st-caps block mb-1.5">{label}</span>
      {children}
      {hint && <span className="st-faint text-xs block mt-1">{hint}</span>}
    </label>
  );
}

export function Tip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="st-tooltip">
      {label != null && <p className="st-faint mb-1">{label}</p>}
      {payload.map((p, i) => <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <b>{fmt ? fmt(p.value, p) : p.value}</b></p>)}
    </div>
  );
}
